import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFAQ, setShowFAQ] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openLineWithKeyword = () => {
    window.open('https://lin.ee/gWNMekM?text=中学準備', '_blank');
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/45d2842656097bc6efcba2f348c35c17.jpeg" alt="教室ロゴ" className="h-12 w-auto" />
            <div className="hidden lg:block">
              <p className="text-base text-gray-700 font-semibold leading-tight">練馬高野台 フォニックスが学べる子ども英語教室</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('class-overview')} className="text-base text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">クラス内容</button>
            <button onClick={() => scrollToSection('flow')} className="text-base text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">受講の流れ</button>
            <button onClick={() => scrollToSection('faq')} className="text-base text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">よくある質問</button>
            <button onClick={openLineWithKeyword} className="bg-[#06C755] text-white px-6 py-3 rounded-full text-base font-medium hover:bg-[#05b34d] transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-line-fill text-xl"></i>
              LINE相談
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-24 px-6 bg-gradient-to-br from-[#f5f1eb] via-[#faf8f5] to-[#f0ebe3]">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-2xl text-gray-600 mb-6 font-serif">中学受験、お疲れさまでした。</p>
          <h1 className="text-6xl md:text-7xl font-bold text-[#1a3a52] mb-8 leading-tight">中学準備英語クラス</h1>
          <p className="text-2xl text-gray-700 mb-16 font-medium leading-relaxed">英語が"わからない"ではなく、<br />"わかる"でスタートするために</p>
          
          <div className="flex flex-wrap justify-center gap-8 mb-16">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 w-72 transform hover:scale-105 transition-transform">
              <div className="text-base text-gray-500 mb-3 font-medium">対象</div>
              <div className="text-xl font-bold text-[#1a3a52] leading-relaxed">小6受験終了後〜<br />新中学1年生</div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 w-72 transform scale-105 z-10 shadow-xl">
              <div className="text-base text-gray-500 mb-3 font-medium">クラス形式</div>
              <div className="text-xl font-bold text-[#2d5f3f] leading-relaxed">全8回・少人数制<br />期間限定クラス</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 w-72 transform hover:scale-105 transition-transform">
              <div className="text-base text-gray-500 mb-3 font-medium">開始時期</div>
              <div className="text-xl font-bold text-[#1a3a52] leading-relaxed">2月第2週スタート<br />3月末まで</div>
            </div>
          </div>

          <p className="text-xl text-gray-700 mb-10 max-w-2xl mx-auto leading-relaxed font-medium">英語経験がなくても大丈夫。<br />中学英語の土台を、ていねいに整えていくクラスです。</p>

          <div className="flex flex-wrap justify-center gap-6">
            <button onClick={openLineWithKeyword} className="bg-[#06C755] text-white px-10 py-5 rounded-full text-lg font-semibold hover:bg-[#05b34d] transition-colors flex items-center gap-3 whitespace-nowrap cursor-pointer shadow-lg">
              <i className="ri-line-fill text-2xl"></i>
              LINEで相談する
            </button>
            <button onClick={() => scrollToSection('class-overview')} className="text-gray-700 px-10 py-5 rounded-full text-lg font-semibold hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer border-2 border-gray-300 hover:border-gray-400">クラス詳細を見る</button>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-24 px-6 bg-[#f8f8f8]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <span className="text-sm text-gray-500 uppercase tracking-wider font-semibold">WHO</span>
          </div>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-[#1a3a52] mb-10 leading-tight">
                英語が、これから始まる。<br />
                最初でつまずきたくない、と<br />
                感じている方へ。
              </h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p className="text-xl">中学受験に集中していて、<br />英語はほぼ未経験</p>
                <p className="text-xl">アルファベットは見たことがあるけれど、<br />読み書きに自信がない</p>
                <p className="text-xl">小さい頃に少し触れたけれど、<br />ほとんど覚えていない</p>
                <p className="font-bold text-gray-900 mt-10 text-2xl">そんなお子さんと保護者の方のための、<br />準備クラスです。</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="w-full h-72 rounded-2xl overflow-hidden">
                <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/e67704c231cdff579f62f99d80273368.jpeg" alt="落ち着いた学習環境" className="w-full h-full object-cover object-top" />
              </div>
              <div className="w-full h-72 rounded-2xl overflow-hidden">
                <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/ca70e04379e481afeff0756b74eb9a15.png" alt="安心して学ぶ様子" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Class Purpose Section */}
      <section id="class-overview" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
              <span className="text-gray-900">このクラスの目的は、</span><br />
              <span className="text-[#2d5f3f]">つまずかない状態をつくること。</span>
            </h2>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">中学英語を"いきなり難しい教科"ではなく、<br />"最初に整えれば、あとが楽になる教科"にします。</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-[#faf8f5] rounded-2xl p-10">
              <div className="w-20 h-20 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-book-open-line text-4xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">基礎</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">フォニックスで<br />読める</h3>
              <p className="text-lg text-gray-600 leading-relaxed">単語が読めない不安を解消</p>
            </div>

            <div className="bg-[#faf8f5] rounded-2xl p-10">
              <div className="w-20 h-20 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-arrow-left-right-line text-4xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">理解重視</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">語順が<br />わかる</h3>
              <p className="text-lg text-gray-600 leading-relaxed">英文の仕組みを理解する</p>
            </div>

            <div className="bg-[#faf8f5] rounded-2xl p-10">
              <div className="w-20 h-20 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-lightbulb-line text-4xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">納得感</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">ルールを<br />知る</h3>
              <p className="text-lg text-gray-600 leading-relaxed">丸暗記ではなく、納得して進む</p>
            </div>

            <div className="bg-[#faf8f5] rounded-2xl p-10">
              <div className="w-20 h-20 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-4xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">安心</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">授業に<br />ついていける</h3>
              <p className="text-lg text-gray-600 leading-relaxed">学校の英語を安心して受けられる</p>
            </div>

            <div className="bg-[#faf8f5] rounded-2xl p-10">
              <div className="w-20 h-20 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-star-line text-4xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">自信</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">英語が<br />楽しくなる</h3>
              <p className="text-lg text-gray-600 leading-relaxed">わかるから、続けられる</p>
            </div>

            <div className="bg-[#faf8f5] rounded-2xl p-10">
              <div className="w-20 h-20 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-heart-line text-4xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">余裕</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">中学生活に<br />余裕が生まれる</h3>
              <p className="text-lg text-gray-600 leading-relaxed">英語の不安が減ることで、他の教科や学校生活にも前向きに向き合える</p>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Content Section */}
      <section className="py-24 px-6 bg-[#1a3a52]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 text-center">
            <span className="border border-white/30 text-white px-5 py-2 rounded-full text-sm uppercase tracking-wider inline-block mb-8 font-semibold">CURRICULUM</span>
            <h2 className="text-5xl md:text-6xl font-bold text-white leading-tight">覚えさせるより、<br />わかるようにする。</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-[#faf8f5] rounded-2xl overflow-hidden">
              <div className="w-full h-56">
                <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/ca70e04379e481afeff0756b74eb9a15.png" alt="フォニックス学習" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-8 bg-[#1a3a52]">
                <div className="text-base text-white/70 mb-3 font-medium">フォニックス</div>
                <h3 className="text-2xl font-bold text-white leading-tight">読み・書き・理解の土台</h3>
              </div>
            </div>

            <div className="bg-[#faf8f5] rounded-2xl overflow-hidden">
              <div className="w-full h-56">
                <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/ddc28b2263c0c46f48b2b8c621f8e85d.png" alt="語順の理解" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-8 bg-[#1a3a52]">
                <div className="text-base text-white/70 mb-3 font-medium">語順の考え方</div>
                <h3 className="text-2xl font-bold text-white leading-tight">文法用語より、仕組みを重視</h3>
              </div>
            </div>

            <div className="bg-[#faf8f5] rounded-2xl overflow-hidden">
              <div className="w-full h-56">
                <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/ddc28b2263c0c46f48b2b8c621f8e85d.png" alt="少人数指導" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-8 bg-[#1a3a52]">
                <div className="text-base text-white/70 mb-3 font-medium">少人数で確認</div>
                <h3 className="text-2xl font-bold text-white leading-tight">わかったつもりで進ませない</h3>
              </div>
            </div>

            <div className="bg-[#faf8f5] rounded-2xl overflow-hidden">
              <div className="w-full h-56">
                <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/ca70e04379e481afeff0756b74eb9a15.png" alt="中学準備" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-8 bg-[#1a3a52]">
                <div className="text-base text-white/70 mb-3 font-medium">中1授業への準備</div>
                <h3 className="text-2xl font-bold text-white leading-tight">スムーズに理解できる状態へ</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Class Details Section */}
      <section className="py-24 px-6 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-5xl font-bold mb-6">
              <span className="text-gray-900">クラス詳細</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">2月第2週スタート、3月末まで。<br />定員に達し次第、募集終了します。</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl p-10 shadow-sm">
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-calendar-line text-3xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">曜日・時間</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">水曜日</h3>
              <p className="text-2xl text-gray-900 mb-4">18:20〜19:20</p>
              <div className="text-base text-gray-500">60分</div>
            </div>

            <div className="bg-white rounded-2xl p-10 shadow-sm">
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-time-line text-3xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">期間・回数</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">2月第2週〜3月末</h3>
              <p className="text-2xl text-gray-900 mb-4">全8回</p>
              <div className="text-base text-gray-500">期間限定</div>
            </div>

            <div className="bg-white rounded-2xl p-10 shadow-sm">
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-group-line text-3xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">定員</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">少人数制</h3>
              <p className="text-xl text-gray-900 mb-4">定員あり</p>
              <div className="text-base text-gray-500">先着順</div>
            </div>

            <div className="bg-white rounded-2xl p-10 shadow-sm">
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-map-pin-line text-3xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">実施形態</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">対面基本</h3>
              <p className="text-xl text-gray-900 mb-4">状況により対応可</p>
              <div className="text-base text-gray-500">柔軟対応</div>
            </div>

            <div className="bg-white rounded-2xl p-10 shadow-sm">
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-user-heart-line text-3xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">対象</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">中学英語のスタートに<br />不安があるお子さん</h3>
              <p className="text-base text-gray-600 mb-4">受験の有無は問いません</p>
              <div className="text-base text-gray-500">どなたでも</div>
            </div>

            <div className="bg-white rounded-2xl p-10 shadow-sm">
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <i className="ri-price-tag-3-line text-3xl text-[#2d5f3f]"></i>
              </div>
              <div className="text-base text-gray-500 mb-3 font-medium">受講料</div>
              <h3 className="text-4xl font-bold text-gray-900 mb-2">44,000円</h3>
              <p className="text-xl text-gray-600 mb-4">（税込）</p>
              <div className="text-base text-gray-500">全8回分</div>
            </div>
          </div>
        </div>
      </section>

      {/* Flow Section */}
      <section id="flow" className="py-24 px-6 bg-[#faf8f5]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <span className="text-sm text-gray-500 uppercase tracking-wider font-semibold">FLOW</span>
          </div>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-[#1a3a52] mb-10 leading-tight">
                体験レッスンは、<br />
                実施していません。<br />
                まずは、LINEで<br />
                ご相談ください。
              </h2>
              <div className="space-y-6 text-gray-700 leading-relaxed mb-12">
                <p className="text-xl">お申込み前に、これまでの英語経験や<br />中学英語への不安をヒアリングします。</p>
                <p className="text-xl">クラスが合いそうか、一緒に確認してから進めますので、<br />無理な勧誘はありません。</p>
                <p className="font-bold text-gray-900 text-2xl mt-8">まずは相談からで大丈夫です。</p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 border-2 border-[#06C755]/20">
                <div className="flex items-start gap-6 mb-6">
                  <div className="w-16 h-16 bg-[#06C755]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-fill text-3xl text-[#06C755]"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">LINE公式アカウントで相談</h3>
                    <p className="text-base text-gray-600 mb-6 leading-relaxed">友だち追加後、以下のキーワードを送信してください</p>
                    <div className="bg-[#f8f8f8] rounded-lg p-6 border border-gray-200">
                      <p className="text-sm text-gray-500 mb-2">送信キーワード</p>
                      <p className="text-2xl font-bold text-[#1a3a52]">中学準備</p>
                    </div>
                  </div>
                </div>
                <button onClick={openLineWithKeyword} className="w-full bg-[#06C755] text-white py-4 rounded-full text-lg font-semibold hover:bg-[#05b34d] transition-colors flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer">
                  <i className="ri-line-fill text-2xl"></i>
                  LINEで相談する
                </button>
              </div>
            </div>
            <div className="space-y-6">
              <div className="w-full h-80 rounded-2xl overflow-hidden">
                <img src="https://public.readdy.ai/ai/img_res/01b9ffc4e6afffc2b9c7b4c82b10a222.jpg" alt="LINEでの相談" className="w-full h-full object-cover object-top" />
              </div>
              <div className="w-full h-80 rounded-2xl overflow-hidden">
                <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/a69f42d1b6c08f77a2c5abf0d9eaa2fa.png" alt="安心して相談" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 bg-[#1a3a52]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 text-center">
            <span className="border border-white/30 text-white px-5 py-2 rounded-full text-sm uppercase tracking-wider inline-block mb-8 font-semibold">FAQ</span>
            <h2 className="text-5xl md:text-6xl font-bold text-white leading-tight">よくあるご質問</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-[#faf8f5] rounded-2xl p-10 cursor-pointer hover:shadow-xl transition-shadow" onClick={() => setShowFAQ(showFAQ === 1 ? null : 1)}>
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <span className="text-3xl font-bold text-[#2d5f3f]">Q</span>
              </div>
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">英語経験ゼロでも<br />大丈夫？</h3>
                <p className="text-lg text-gray-600">はい、大丈夫です</p>
              </div>
              {showFAQ === 1 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-base text-gray-600 leading-relaxed">このクラスは、英語経験がないお子さんを前提に設計しています。アルファベットから丁寧に始めますので、安心してご参加ください。</p>
                </div>
              )}
            </div>

            <div className="bg-[#faf8f5] rounded-2xl p-10 cursor-pointer hover:shadow-xl transition-shadow" onClick={() => setShowFAQ(showFAQ === 2 ? null : 2)}>
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <span className="text-3xl font-bold text-[#2d5f3f]">Q</span>
              </div>
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">宿題は<br />ありますか？</h3>
                <p className="text-lg text-gray-600">少量、無理のない範囲で</p>
              </div>
              {showFAQ === 2 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-base text-gray-600 leading-relaxed">受験後のお子さんの状態を考慮し、負担にならない程度の復習課題をお出しします。量より質を重視しています。</p>
                </div>
              )}
            </div>

            <div className="bg-[#faf8f5] rounded-2xl p-10 cursor-pointer hover:shadow-xl transition-shadow" onClick={() => setShowFAQ(showFAQ === 3 ? null : 3)}>
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <span className="text-3xl font-bold text-[#2d5f3f]">Q</span>
              </div>
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">途中参加は<br />可能？</h3>
                <p className="text-lg text-gray-600">定員状況により相談可</p>
              </div>
              {showFAQ === 3 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-base text-gray-600 leading-relaxed">クラスの進行状況と定員状況によっては、途中参加も可能です。まずはLINEでご相談ください。</p>
                </div>
              )}
            </div>

            <div className="bg-[#faf8f5] rounded-2xl p-10 cursor-pointer hover:shadow-xl transition-shadow" onClick={() => setShowFAQ(showFAQ === 4 ? null : 4)}>
              <div className="w-16 h-16 bg-[#2d5f3f]/10 rounded-xl flex items-center justify-center mb-8">
                <span className="text-3xl font-bold text-[#2d5f3f]">Q</span>
              </div>
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">オンライン対応は？</h3>
                <p className="text-lg text-gray-600">状況により対応可能</p>
              </div>
              {showFAQ === 4 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-base text-gray-600 leading-relaxed">基本は対面ですが、体調不良や天候などの状況に応じて、オンラインでの受講も可能です。</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/ddc28b2263c0c46f48b2b8c621f8e85d.png" alt="温かい教室風景" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="text-center">
            <h2 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-8">
              英語が、わかるで<br />スタートする。
            </h2>
            <p className="text-2xl text-white font-light mb-10 leading-relaxed max-w-3xl mx-auto">
              まずは、LINEでご相談ください。<br />
              お子さんの状況を伺いながら、一緒に確認していきます。
            </p>
            <button onClick={openLineWithKeyword} className="inline-flex items-center gap-4 bg-white text-gray-900 px-10 py-5 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer shadow-xl">
              <span>LINEで相談する</span>
              <div className="w-12 h-12 bg-[#06C755] rounded-full flex items-center justify-center">
                <i className="ri-line-fill text-white text-xl"></i>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2d5f3f] text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg viewBox="0 0 1200 400" className="w-full h-full">
            <path d="M0,200 Q300,100 600,200 T1200,200" fill="none" stroke="currentColor" strokeWidth="2"/>
            <path d="M0,250 Q300,150 600,250 T1200,250" fill="none" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-5 gap-12 mb-16">
            <div className="md:col-span-2">
              <div className="mb-8">
                <img src="https://static.readdy.ai/image/c803d892d8e77df311d98a777f12c462/45d2842656097bc6efcba2f348c35c17.jpeg" alt="教室ロゴ" className="h-14 w-auto brightness-0 invert" />
              </div>
              <p className="text-white/80 text-base leading-relaxed mb-8">中学英語の土台を、ていねいに整える。<br />お子さんが安心してスタートできるように。</p>
            </div>

            <div>
              <h3 className="font-semibold mb-6 text-lg">クラス情報</h3>
              <ul className="space-y-3 text-base text-white/80">
                <li><button onClick={() => scrollToSection('class-overview')} className="hover:text-white transition-colors cursor-pointer">対象者</button></li>
                <li><button onClick={() => scrollToSection('class-overview')} className="hover:text-white transition-colors cursor-pointer">内容</button></li>
                <li><button onClick={() => scrollToSection('class-overview')} className="hover:text-white transition-colors cursor-pointer">詳細</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-6 text-lg">受講について</h3>
              <ul className="space-y-3 text-base text-white/80">
                <li><button onClick={() => scrollToSection('flow')} className="hover:text-white transition-colors cursor-pointer">流れ</button></li>
                <li><button onClick={() => scrollToSection('class-overview')} className="hover:text-white transition-colors cursor-pointer">料金</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-white transition-colors cursor-pointer">FAQ</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-6 text-lg">お問い合わせ</h3>
              <button onClick={openLineWithKeyword} className="bg-[#06C755] text-white px-6 py-3 rounded-full text-base font-medium hover:bg-[#05b34d] transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-line-fill text-xl"></i>
                LINE相談
              </button>
            </div>
          </div>

          <div className="pt-10 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-base text-white/60">© 2025 中学準備英語クラス. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 hover:text-white/80 transition-colors">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
